import { CSSProperties, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { detectChannelType } from "../../common/device";

/**
 * On-screen console for phone testing. Phones give you no devtools, so every diagnostic this app
 * writes (console.*, uncaught errors, rejected promises) is mirrored here, on top of a fixed
 * environment report — secure-context / getUserMedia / MediaSource support, which is what actually
 * decides whether the mic and the audio path can work at all.
 *
 * "Copy" puts the whole report + log on the clipboard so it can be pasted somewhere useful.
 *
 * Two switches, both off by default in the sense that nothing renders unless asked for:
 *
 *  1. Build-level — `REACT_APP_DEBUG_OVERLAY="false"` in the env file kills it outright: the
 *     overlay can't be turned on from the URL at all. Set that in the builds you ship to users.
 *  2. Session-level — `?debug=1` turns it on and remembers it (so it survives the reloads and
 *     in-app navigation you do while testing); `?debug=0`, or the × button, turns it off again.
 */

const MAX_ENTRIES = 300;
const STORAGE_KEY = "debug-overlay";

/** Build-level kill switch. Any value other than "false" leaves ?debug=1 available. */
const ALLOWED = process.env.REACT_APP_DEBUG_OVERLAY !== "false";

const readEnabled = (): boolean => {
  if (!ALLOWED || typeof window === "undefined") return false;

  const param = new URLSearchParams(window.location.search).get("debug");
  if (param === "1") {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* private mode — the param alone still enables it for this page */
    }
    return true;
  }
  if (param === "0") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to clean up */
    }
    return false;
  }

  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

type Level = "log" | "info" | "warn" | "error";

interface Entry {
  id: number;
  level: Level;
  time: string;
  text: string;
}

const LEVEL_COLOR: Record<Level, string> = {
  log: "#d6d6d6",
  info: "#7fd1ff",
  warn: "#ffc861",
  error: "#ff8b8b",
};

const format = (args: unknown[]): string =>
  args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(" ");

const supports = (mime: string): boolean => {
  try {
    return (
      typeof MediaSource !== "undefined" &&
      typeof MediaSource.isTypeSupported === "function" &&
      MediaSource.isTypeSupported(mime)
    );
  } catch {
    return false;
  }
};

const recorderSupports = (mime: string): boolean => {
  try {
    return (
      typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(mime)
    );
  } catch {
    return false;
  }
};

export function MobileDebugOverlay() {
  const [enabled, setEnabled] = useState(readEnabled);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [copied, setCopied] = useState(false);
  const nextId = useRef(0);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const report = useMemo(() => {
    if (!enabled) return [] as Array<[string, string]>;
    const nav = typeof navigator !== "undefined" ? navigator : ({} as Navigator);
    return [
      ["origin", `${window.location.protocol}//${window.location.host}`],
      ["secureContext", String(window.isSecureContext)],
      // Cast: lib.dom types mediaDevices/getUserMedia as always present, but on an insecure
      // origin they genuinely are undefined at runtime — which is exactly what we're reporting.
      [
        "getUserMedia",
        typeof (nav as any).mediaDevices?.getUserMedia === "function" ? "yes" : "NO",
      ],
      [
        "SpeechRecognition",
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
          ? "yes"
          : "NO",
      ],
      ["MediaRecorder", typeof MediaRecorder !== "undefined" ? "yes" : "NO"],
      ["rec webm/opus", recorderSupports("audio/webm;codecs=opus") ? "yes" : "no"],
      ["rec mp4", recorderSupports("audio/mp4") ? "yes" : "no"],
      ["MediaSource", typeof MediaSource !== "undefined" ? "yes" : "NO"],
      ["MSE audio/mpeg", supports("audio/mpeg") ? "yes" : "no"],
      // What the app itself thinks you are — this drives the buffered-audio path and the
      // Auto-Detect-only speaker language.
      ["device", detectChannelType()],
      ["touchPoints", String(nav.maxTouchPoints ?? 0)],
      ["screen", `${window.innerWidth}x${window.innerHeight}`],
      ["whisperWs", process.env.REACT_APP_WHISPER_WS_URL || "(derived)"],
      ["ua", nav.userAgent || "?"],
    ] as Array<[string, string]>;
  }, [enabled]);

  // Mirror console output + uncaught failures into the panel.
  useEffect(() => {
    if (!enabled) return;

    const push = (level: Level, args: unknown[]) => {
      const text = format(args);
      setEntries((prev) => {
        const entry: Entry = {
          id: nextId.current++,
          level,
          time: new Date().toLocaleTimeString([], { hour12: false }),
          text,
        };
        const next = [...prev, entry];
        return next.length > MAX_ENTRIES ? next.slice(-MAX_ENTRIES) : next;
      });
    };

    const levels: Level[] = ["log", "info", "warn", "error"];
    const originals = levels.map((level) => {
      const original = console[level];
      console[level] = (...args: unknown[]) => {
        push(level, args);
        original.apply(console, args as any);
      };
      return { level, original };
    });

    const onError = (event: ErrorEvent) =>
      push("error", [`uncaught: ${event.message}`, `${event.filename}:${event.lineno}`]);
    const onRejection = (event: PromiseRejectionEvent) =>
      push("error", ["unhandled rejection:", event.reason]);

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      originals.forEach(({ level, original }) => {
        console[level] = original;
      });
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, [enabled]);

  // Keep the newest line in view.
  useEffect(() => {
    if (collapsed) return;
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [entries, collapsed]);

  if (!enabled) return null;

  const asText = () =>
    [
      ...report.map(([key, value]) => `${key}: ${value}`),
      "---",
      ...entries.map((e) => `${e.time} [${e.level}] ${e.text}`),
    ].join("\n");

  // Turning it off here also clears the remembered flag, so it stays off across reloads until
  // ?debug=1 asks for it again.
  const disable = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to clean up */
    }
    setEnabled(false);
  };

  const copy = async () => {
    const text = asText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API needs a secure context — fall back to a selectable prompt.
      window.prompt("Copy the debug report:", text);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483647,
        background: "rgba(8,10,14,0.94)",
        color: "#eaeaea",
        font: "11px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace",
        borderTop: "1px solid #3a4150",
        maxHeight: collapsed ? undefined : "45vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 8px",
          borderBottom: collapsed ? "none" : "1px solid #3a4150",
        }}
      >
        <strong style={{ flex: 1 }}>debug · {entries.length}</strong>
        <button onClick={copy} style={buttonStyle}>
          {copied ? "copied" : "copy"}
        </button>
        <button onClick={() => setEntries([])} style={buttonStyle}>
          clear
        </button>
        <button onClick={() => setCollapsed((c) => !c)} style={buttonStyle}>
          {collapsed ? "show" : "hide"}
        </button>
        <button onClick={disable} style={buttonStyle} title="Turn the overlay off">
          ×
        </button>
      </div>

      {!collapsed && (
        <div ref={bodyRef} style={{ overflowY: "auto", padding: "6px 8px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: 8,
              paddingBottom: 6,
              marginBottom: 6,
              borderBottom: "1px dashed #3a4150",
            }}
          >
            {report.map(([key, value]) => (
              <Fragment key={key}>
                <span style={{ color: "#8d97a8" }}>{key}</span>
                <span
                  style={{
                    color: value === "NO" ? "#ff8b8b" : "#eaeaea",
                    wordBreak: "break-all",
                  }}
                >
                  {value}
                </span>
              </Fragment>
            ))}
          </div>

          {entries.map((entry) => (
            <div
              key={entry.id}
              style={{ color: LEVEL_COLOR[entry.level], wordBreak: "break-word" }}
            >
              <span style={{ color: "#6c7688" }}>{entry.time} </span>
              {entry.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const buttonStyle: CSSProperties = {
  background: "#232937",
  color: "#eaeaea",
  border: "1px solid #3a4150",
  borderRadius: 4,
  padding: "3px 8px",
  font: "inherit",
};

export default MobileDebugOverlay;
